<script setup>
import { computed, nextTick, onBeforeUnmount, ref } from 'vue'
import { usePlaybackStore } from '@state/stores/playback'
import NavTooltip from '@components/navigation/nav-tooltip'
import StationMenu from './station-menu'
import { STREAMING_STATUSES } from '@data/stations'

// Same hover intent as the nav rail tooltips (nav-item.vue).
const HOVER_INTENT_MS = 300

const playbackStore = usePlaybackStore()
const isOpen = ref(false)
const isTooltipVisible = ref(false)
const wrap = ref(null)
const trigger = ref(null)
let intentTimer = null
// Set while the menu hands focus back: that focus is not the listener hovering or tabbing
// to the launcher, and nothing (no mouseleave) would hide a tooltip it opened.
let suppressFocusTooltip = false

const isStreaming = computed(() => {
    const resource = playbackStore.resource
    return Boolean(resource) && resource.type === 'channel' && STREAMING_STATUSES.includes(playbackStore.status)
})

function hideTooltip() {
    clearTimeout(intentTimer)
    isTooltipVisible.value = false
}

// A click with detail 0 came from the keyboard (Enter/Space): move focus into the menu,
// as _base-context-menu.vue does. A pointer open leaves focus where it was.
function toggle(event) {
    isOpen.value = !isOpen.value
    hideTooltip()
    if (!isOpen.value || !event || event.detail !== 0) return
    nextTick(() => {
        const first = wrap.value && wrap.value.querySelector('[role="menuitem"]')
        first && first.focus()
    })
}

function onMenuClose({ restoreFocus = false } = {}) {
    if (!isOpen.value) return
    isOpen.value = false
    if (!restoreFocus) return
    nextTick(() => {
        if (!trigger.value) return
        suppressFocusTooltip = true
        trigger.value.focus({ preventScroll: true })
        // Focus events fire synchronously; clear it even if no focus event came (already focused).
        suppressFocusTooltip = false
    })
}

// Focus left the launcher and its menu (Tab past the last station, say): close quietly.
// Only a known destination counts — Safari doesn't focus a clicked <button>, so a
// pointer press on a station blurs with no relatedTarget and must not close the menu
// before its click lands.
function onFocusout(event) {
    const next = event.relatedTarget
    if (!isOpen.value || !next || (wrap.value && wrap.value.contains(next))) return
    onMenuClose({ restoreFocus: false })
}

function showTooltipSoon() {
    if (suppressFocusTooltip) {
        suppressFocusTooltip = false
        return
    }
    if (isOpen.value) return
    clearTimeout(intentTimer)
    intentTimer = setTimeout(() => {
        isTooltipVisible.value = !isOpen.value
    }, HOVER_INTENT_MS)
}

onBeforeUnmount(() => clearTimeout(intentTimer))
</script>

<template>
    <div
        ref="wrap"
        class="station-launcher-wrap"
        @focusout="onFocusout"
    >
        <!-- Its own positioned box, so the tooltip centres on the 40px disc, not the padded wrap. -->
        <span class="station-launcher-anchor">
            <button
                ref="trigger"
                type="button"
                :class="['station-launcher', { 'station-launcher-streaming': isStreaming, 'station-launcher-open': isOpen }]"
                aria-label="Live radio"
                aria-haspopup="menu"
                :aria-expanded="isOpen ? 'true' : 'false'"
                @click="toggle"
                @mouseenter="showTooltipSoon"
                @mouseleave="hideTooltip"
                @focus="showTooltipSoon"
                @blur="hideTooltip"
                @keydown.esc="hideTooltip"
            >
                <BaseIcon
                    name="radio"
                    :filled="isStreaming"
                    :size="24"
                />
            </button>
            <NavTooltip
                text="Live radio"
                placement="bottom"
                :visible="isTooltipVisible"
                aria-hidden="true"
            />
        </span>
        <StationMenu
            :open="isOpen"
            :anchor="trigger"
            @close="onMenuClose"
        />
    </div>
</template>

<style lang="scss" scoped>
@use '@design' as *;

$launcher: 40px;
$divider-height: 24px;

.station-launcher-wrap {
    position: relative;
    display: flex;
    align-items: center;
    // Full pill height, so the menu's top: calc(100% + 8px) is 8 below the PILL (D27).
    align-self: stretch;
    padding-left: $space-8;

    // Hairline divider, 24 tall, centred on the pill (Figma Search/Launcher 547:1558).
    &::before {
        position: absolute;
        top: 50%;
        left: 0;
        width: 1px;
        height: $divider-height;
        content: '';
        background: $c-border-default;
        transform: translateY(-50%);
    }
}

.station-launcher-anchor {
    position: relative;
    display: inline-flex;
}

.station-launcher {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    color: $c-text-secondary;
    cursor: pointer;
    background: transparent;
    border: 0;
    border-radius: $radius-full;

    @include square($launcher);
    @include transition((color, background-color), fast);
    @include focusRing;

    &.station-launcher-open {
        color: $c-text-primary;
        background: $c-state-hover;
    }

    // Tablets render the launcher — keep the highlight off after a tap.
    @media (hover: hover) {
        &:hover {
            color: $c-text-primary;
            background: $c-state-hover;
        }
    }
}

.station-launcher-streaming,
.station-launcher-streaming.station-launcher-open {
    color: $c-accent-default;

    @media (hover: hover) {
        &:hover {
            color: $c-accent-default;
        }
    }
}
</style>
